import { Component, type ErrorInfo, type ReactNode } from "react";
import { FaTriangleExclamation } from "react-icons/fa6";
import { renderIcon } from "./icons";

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
};

/**
 * Last-resort protection for render failures anywhere in the client app.
 * Feature-level code should still handle its own expected states.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Unhandled application render error", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="flex min-h-screen items-center justify-center bg-[rgb(var(--color-bg))] p-6 text-center text-[rgb(var(--color-fg))]">
          <div className="max-w-md space-y-3">
            <div className="flex justify-center text-red-600" aria-hidden="true">
              {renderIcon(FaTriangleExclamation, "h-10 w-10")}
            </div>
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-[rgb(var(--color-muted))]">
              Please reload the page. If this keeps happening, contact{" "}
              <a
                className="font-semibold text-[rgb(var(--color-accent))] underline"
                href="mailto:support@vendistri.com"
              >
                support@vendistri.com
              </a>
              .
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-[rgb(var(--color-accent))] px-4 py-2 text-sm font-semibold text-white"
            >
              Reload page
            </button>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}
